import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";
import { api, type Video } from "../lib/api";
import { VideoEmbed } from "../components/VideoEmbed";
import { VersionSelector } from "../components/VersionSelector";
import { DigestBody } from "../components/DigestBody";
import { EngagementChart } from "../components/EngagementChart";
import { VideoCommentIntelligence } from "../components/VideoCommentIntelligence";
import { DigestCallout } from "../components/youtube/explainers";

/**
 * Phase 5c — per-video reading surface.
 * Composition top to bottom:
 *   1. Lite YouTube embed (16:9, max 640px wide, click-to-mount iframe)
 *   2. Header strip — title (link out), publish date + meta, status chips
 *   3. Version selector (default Latest; URL-routed for sharing)
 *   4. Digest body (serif markdown, .digest-body styling)
 *   5. Engagement chart (small multiples, range presets)
 */
export function VideoDetail() {
  const { videoId, version } = useParams<{
    videoId: string;
    version?: string;
  }>();

  const videoQ = useQuery({
    queryKey: ["video", videoId],
    queryFn: () => api.video(videoId!),
    enabled: !!videoId,
  });

  const versionsQ = useQuery({
    queryKey: ["digestVersions", videoId],
    queryFn: () => api.digestVersions(videoId!),
    enabled: !!videoId,
  });

  const digestQ = useQuery({
    queryKey: ["digest", videoId, version ?? "latest"],
    queryFn: () => api.digest(videoId!, version ?? "latest"),
    enabled: !!videoId,
    retry: (failureCount, err) => {
      if (err instanceof Error && err.message.includes("404")) return false;
      return failureCount < 1;
    },
  });

  const metricsQ = useQuery({
    queryKey: ["metrics", videoId],
    queryFn: () => api.metrics(videoId!),
    enabled: !!videoId,
  });

  if (!videoId) return null;

  if (videoQ.isLoading) {
    return (
      <div className="space-y-4">
        <div className="aspect-video w-full max-w-[640px] animate-pulse rounded-lg bg-black/5" />
        <div className="h-7 w-2/3 animate-pulse rounded bg-black/5" />
        <div className="h-4 w-1/2 animate-pulse rounded bg-black/5" />
      </div>
    );
  }

  if (!videoQ.data) {
    return (
      <div className="rounded-xl border border-white/10 bg-terminal p-6 text-sm text-terminal-ink-muted">
        Couldn't load this video.
      </div>
    );
  }

  const video = videoQ.data;
  const has404Digest =
    digestQ.error instanceof Error && digestQ.error.message.includes("404");

  return (
    <article>
      {/* key forces remount on videoId change so internal state (thumbSrc fallback, playing) resets */}
      <VideoEmbed key={video.videoId} videoId={video.videoId} />

      <Header video={video} />

      {versionsQ.data && versionsQ.data.length > 0 && (
        <VersionSelector
          videoId={video.videoId}
          versions={versionsQ.data}
          current={version}
        />
      )}

      {/*
        Two-column from xl up: digest in the reading column, chart in a
        sticky right rail. Below xl falls back to single stacked column
        (chart slides under the digest with the same gap-10 spacing).
        xl=1280px viewport gives the digest enough breathing room once
        sidebar (280) + chat rail (32-360) chrome is accounted for.
      */}
      <div className="grid grid-cols-1 gap-10 xl:grid-cols-[minmax(0,1fr)_320px] xl:items-start">
        <div>
          <DigestSection
            loading={digestQ.isLoading}
            markdown={digestQ.data?.markdown}
            notFound={has404Digest}
            video={video}
          />
          {/*
            Phase 6d.2 — per-video classification + theme breakdown. Sits
            inside the digest column so the chart can stay in its sticky
            right rail at xl+; below xl the natural stacked order is
            digest → comments card → chart (matching the spec).
          */}
          <VideoCommentIntelligence videoId={video.videoId} />
        </div>

        <div className="xl:sticky xl:top-6">
          <EngagementChart
            metrics={metricsQ.data}
            isLoading={metricsQ.isLoading}
          />
        </div>
      </div>
    </article>
  );
}

/* -------------------------------------------------------------------------- */
/* Header strip                                                               */
/* -------------------------------------------------------------------------- */

function Header({ video }: { video: Video }) {
  const watchUrl = `https://www.youtube.com/watch?v=${video.videoId}`;
  const published = video.publishedAt
    ? new Date(video.publishedAt).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "—";

  return (
    <header className="mb-5">
      <h1 className="text-[26px] font-bold leading-tight tracking-tight text-ink">
        <a
          href={watchUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-baseline gap-2 hover:text-brand-indigo"
        >
          {video.title || video.videoId}
          <ExternalLink size={14} className="shrink-0 self-center opacity-50" />
        </a>
      </h1>

      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-ink-muted">
        <span>Published {published}</span>
        <span aria-hidden className="text-ink-muted/50">
          ·
        </span>
        <Chip>transcript {video.transcriptStatus || "—"}</Chip>
        {video.lastDigestAt ? (
          <Chip>summary updated {formatRelative(video.lastDigestAt)}</Chip>
        ) : (
          <Chip>no summary yet</Chip>
        )}
        {video.newCommentsSinceLastDigest > 0 && (
          <Chip>+{video.newCommentsSinceLastDigest} new comments</Chip>
        )}
      </div>
      <div className="mt-2">
        <DigestCallout tone="light" />
      </div>
    </header>
  );
}

function Chip({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-full border border-rule px-2 py-0.5 text-[11px] font-medium text-ink-muted">
      {children}
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/* Digest section                                                             */
/* -------------------------------------------------------------------------- */

function DigestSection({
  loading,
  markdown,
  notFound,
  video,
}: {
  loading: boolean;
  markdown: string | undefined;
  notFound: boolean;
  video: Video;
}) {
  if (loading) {
    return (
      <div className="space-y-3">
        <div className="h-4 w-3/4 animate-pulse rounded bg-black/5" />
        <div className="h-4 w-full animate-pulse rounded bg-black/5" />
        <div className="h-4 w-5/6 animate-pulse rounded bg-black/5" />
        <div className="h-4 w-4/6 animate-pulse rounded bg-black/5" />
      </div>
    );
  }

  if (notFound) {
    const newSince = video.newCommentsSinceLastDigest;
    return (
      <div className="rounded-xl border border-white/10 bg-terminal p-5 text-sm">
        <div className="font-medium text-terminal-ink">No summary yet</div>
        <p className="mt-2 text-terminal-ink-muted">
          A summary is written automatically once this video's comments have been
          analysed and the next scheduled summary run happens, so a newer video
          may not have one for a little while.
          {newSince > 0 &&
            ` So far, ${newSince.toLocaleString("en-GB")} comment${
              newSince === 1 ? "" : "s"
            } are waiting to be summarised.`}
        </p>
      </div>
    );
  }

  if (!markdown) {
    return (
      <div className="rounded-xl border border-white/10 bg-terminal p-5 text-sm text-terminal-ink-muted">
        Couldn't load this digest version.
      </div>
    );
  }

  return <DigestBody markdown={markdown} />;
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                     */
/* -------------------------------------------------------------------------- */

function formatRelative(iso: string): string {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const days = Math.floor((Date.now() - then) / (1000 * 60 * 60 * 24));
  if (days < 1) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
  });
}
